import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  CheckCircle,
  Star,
  TrendingUp,
  AccountCircle,
  Build,
  AttachMoney,
  Flag,
} from '@mui/icons-material';
import { Account } from '../types';

interface KeyMilestonesProps {
  account: Account;
  stageHistory?: any;
}

interface Milestone {
  title: string;
  description: string;
  timestamp: string;
  type: 'signup' | 'entity' | 'feature' | 'stage' | 'subscription' | 'achievement';
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | 'grey' | 'inherit';
}

export const KeyMilestones: React.FC<KeyMilestonesProps> = ({ account, stageHistory }) => {
  const generateMilestones = (): Milestone[] => {
    const milestones: Milestone[] = [];

    // 1. Account Creation / Signup
    if (account.metadata?.clientSince) {
      milestones.push({
        title: 'Account Created',
        description: 'Customer signed up and created an account',
        timestamp: account.metadata.clientSince,
        type: 'signup',
        icon: <AccountCircle />,
        color: 'primary',
      });
    } else if (account.createdAt) {
      milestones.push({
        title: 'Account Created',
        description: 'Customer signed up and created an account',
        timestamp: account.createdAt,
        type: 'signup',
        icon: <AccountCircle />,
        color: 'primary',
      });
    }

    // 2. First Entity Created (for each entity type)
    const entities = account.entities || {};
    Object.entries(entities).forEach(([entityType, entityData]) => {
      if (entityData.firstAt) {
        const entityName = entityType.charAt(0).toUpperCase() + entityType.slice(1);
        milestones.push({
          title: `First ${entityName} Created`,
          description: `Customer created their first ${entityType} (Total: ${entityData.count})`,
          timestamp: entityData.firstAt,
          type: 'entity',
          icon: <Build />,
          color: 'success',
        });
      }
    });

    // 3. Feature Adoptions
    const features = account.features || {};
    Object.entries(features).forEach(([featureName, featureData]: [string, any]) => {
      if (featureData.enabled && featureData.firstUsedAt) {
        milestones.push({
          title: `${featureName} Adopted`,
          description: `Customer started using ${featureName}${featureData.usageCount ? ` (Used ${featureData.usageCount} times)` : ''}`,
          timestamp: featureData.firstUsedAt,
          type: 'feature',
          icon: <Star />,
          color: 'info',
        });
      }
    });

    // 4. Stage Transitions
    if (stageHistory?.transitions && stageHistory.transitions.length > 0) {
      stageHistory.transitions.forEach((transition: any) => {
        const stageName = transition.stage.replace(/_/g, ' ').charAt(0).toUpperCase() +
                          transition.stage.replace(/_/g, ' ').slice(1);
        milestones.push({
          title: `Moved to ${stageName}`,
          description: `Account progressed to ${stageName} stage${transition.daysInStage ? ` (${transition.daysInStage} days)` : ''}`,
          timestamp: transition.timestamp,
          type: 'stage',
          icon: <Flag />,
          color: 'warning',
        });
      });
    }

    // 5. Subscription Events
    if (account.subscription) {
      // Field names as written by ingest (subscription.started / plan changes / renewals)
      if (account.subscription.trialStartedAt) {
        milestones.push({
          title: 'Trial Started',
          description: `Started a trial${account.subscription.trialEndsAt ? ` ending ${new Date(account.subscription.trialEndsAt).toLocaleDateString()}` : ''}`,
          timestamp: account.subscription.trialStartedAt,
          type: 'subscription',
          icon: <AttachMoney />,
          color: 'info',
        });
      }

      if (account.subscription.subscribedAt) {
        milestones.push({
          title: 'Subscription Started',
          description: `Subscribed to ${account.subscription.plan || 'plan'}${account.subscription.mrr ? ` at $${account.subscription.mrr}/${account.subscription.interval || 'mo'}` : ''}`,
          timestamp: account.subscription.subscribedAt,
          type: 'subscription',
          icon: <AttachMoney />,
          color: 'success',
        });
      }

      if (account.subscription.planChangedAt) {
        milestones.push({
          title: 'Plan Changed',
          description: `${account.subscription.previousPlan ? `${account.subscription.previousPlan} → ` : ''}${account.subscription.plan || 'new plan'}${account.subscription.mrr ? ` ($${account.subscription.mrr}/${account.subscription.interval || 'mo'})` : ''}`,
          timestamp: account.subscription.planChangedAt,
          type: 'subscription',
          icon: <TrendingUp />,
          color: 'success',
        });
      }

      if (account.subscription.cancelledAt) {
        milestones.push({
          title: 'Subscription Cancelled',
          description: account.subscription.cancelReason ? `Reason: ${account.subscription.cancelReason}` : 'Subscription was cancelled',
          timestamp: account.subscription.cancelledAt,
          type: 'subscription',
          icon: <AttachMoney />,
          color: 'error',
        });
      }

      if (account.subscription.renewedAt) {
        milestones.push({
          title: 'Subscription Renewed',
          description: 'Subscription renewed successfully',
          timestamp: account.subscription.renewedAt,
          type: 'subscription',
          icon: <CheckCircle />,
          color: 'success',
        });
      }
    }

    // 6. Achievement Milestones (based on entity counts)
    Object.entries(entities).forEach(([entityType, entityData]) => {
      const thresholds = [10, 50, 100, 500, 1000];

      thresholds.forEach((threshold) => {
        if (entityData.count >= threshold && entityData.count < threshold * 2) {
          const entityName = entityType.charAt(0).toUpperCase() + entityType.slice(1);
          milestones.push({
            title: `${threshold}+ ${entityName}s`,
            description: `Reached ${threshold} ${entityType}s milestone (Current: ${entityData.count})`,
            timestamp: entityData.lastAt || new Date().toISOString(),
            type: 'achievement',
            icon: <Star />,
            color: 'secondary',
          });
        }
      });
    });

    // Sort by timestamp (newest first)
    return milestones.sort((a, b) =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  };

  const milestones = generateMilestones();

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return date.toLocaleDateString();
  };

  const formatAbsoluteDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (milestones.length === 0) {
    return (
      <Card sx={{ borderRadius: 2, bgcolor: 'grey.50' }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No milestones recorded yet. Milestones will appear as the customer uses the platform.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Timeline position="right">
        {milestones.map((milestone, index) => (
          <TimelineItem key={`${milestone.type}-${milestone.timestamp}-${index}`}>
            <TimelineOppositeContent color="text.secondary" sx={{ flex: 0.3, pt: 2 }}>
              <Typography variant="caption" display="block">
                {formatDate(milestone.timestamp)}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {formatAbsoluteDate(milestone.timestamp)}
              </Typography>
            </TimelineOppositeContent>

            <TimelineSeparator>
              <TimelineDot color={milestone.color} variant="outlined">
                {milestone.icon}
              </TimelineDot>
              {index < milestones.length - 1 && <TimelineConnector />}
            </TimelineSeparator>

            <TimelineContent sx={{ py: 2, px: 2 }}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 2,
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {milestone.title}
                    </Typography>
                    <Chip
                      label={milestone.type}
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {milestone.description}
                  </Typography>
                </CardContent>
              </Card>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    </Box>
  );
};
